import { useLazyQuery, useMutation } from '@apollo/client';
import { useSnackbar } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FETCH_BLOTTER_DETAIL,
  FETCH_SUB_TRADE_REQUESTS_FULL,
} from '../queries';
import type { TradeEntry } from '../types';
import {
  BUCKET_SUB_TRADE_REQUESTS,
  CREATE_SUB_TRADE_REQUESTS,
} from '../utils/mutations';
import type { EnrichedPortfolioReport } from './usePortfolioTableState.types';

const ACTIVE_STR_STATES = ['INITIATED', 'READY', 'REQUESTED'] as const;

function buildReportObjectContext(report: EnrichedPortfolioReport) {
  const isGoal = report.modelPortfolioType === 'GOAL';
  const objectId = report.object?.id;
  return {
    isGoal,
    objectId,
    goalOrSubAccountFilter: isGoal
      ? { goalId: objectId }
      : { subAccountId: objectId },
  };
}

function buildActiveSTRFilter(report: EnrichedPortfolioReport) {
  const { goalOrSubAccountFilter } = buildReportObjectContext(report);
  return {
    ...goalOrSubAccountFilter,
    states: [...ACTIVE_STR_STATES],
  };
}

export function usePortfolioTradeBlotter({
  report,
  organizationId,
  bulkTradingProcess,
  onTradeComplete,
}: {
  report: EnrichedPortfolioReport;
  organizationId: string;
  bulkTradingProcess: string | undefined;
  onTradeComplete: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('portfolioTradingHub');
  const [showTradeBlotter, setShowTradeBlotter] = useState(false);
  const [createSubTradeRequests] = useMutation(CREATE_SUB_TRADE_REQUESTS);
  const [bucketSubTradeRequests] = useMutation(BUCKET_SUB_TRADE_REQUESTS);

  const [fetchBlotterDetail, { data: blotterData, loading: blotterLoading }] =
    useLazyQuery(FETCH_BLOTTER_DETAIL, {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    });

  const [fetchActiveSTRs, { data: strData, loading: strLoading }] =
    useLazyQuery(FETCH_SUB_TRADE_REQUESTS_FULL, {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    });

  const blotterReport =
    blotterData?.fetchPortfolioReports?.portfolioReports?.[0];
  const activeSubTradeRequests =
    strData?.fetchSubTradeRequests?.subTradeRequests || [];

  const openBlotter = () => {
    setShowTradeBlotter(true);
    const { goalOrSubAccountFilter } = buildReportObjectContext(report);
    fetchBlotterDetail({
      variables: {
        input: {
          filter: {
            organizationId,
            ...goalOrSubAccountFilter,
          },
          pagination: { perPage: 1, offSet: 0 },
        },
      },
    });
    fetchActiveSTRs({
      variables: {
        input: {
          filter: buildActiveSTRFilter(report),
          pagination: { perPage: 100, offSet: 0 },
        },
      },
    });
  };

  const onRefreshActiveSTRs = () => {
    fetchActiveSTRs({
      variables: {
        input: {
          filter: buildActiveSTRFilter(report),
          pagination: { perPage: 100, offSet: 0 },
        },
      },
    });
  };

  const onGenerateTrades = async (trades: TradeEntry[]) => {
    try {
      const activeReport = blotterReport || report;
      const holdings = activeReport.holdings || [];
      const defaultSubAccountId =
        activeReport.subAccounts?.[0]?.subAccount?.id ||
        activeReport.object?.id;
      const subTradeRequests = trades
        .filter((t) => t.tradeAmountCents !== 0)
        .map((t) => {
          const holding = holdings[t.holdingIndex];
          const saId =
            t.subAccountId && t.subAccountId !== '__agg__'
              ? t.subAccountId
              : defaultSubAccountId;
          return {
            subAccountId: saId,
            financialProductId: holding?.financialProduct?.id,
            portfolioReportId: activeReport.id,
            type: t.side === 'BUY' ? 'BUY' : 'SELL',
            moneyAllocatedCents: Math.abs(t.tradeAmountCents),
            currency: t.currency,
          };
        })
        .filter((r) => r.financialProductId);

      if (subTradeRequests.length === 0) return;

      const source =
        activeReport.modelPortfolioType === 'GOAL' ? 'GOAL' : 'SUB_ACCOUNT';
      const sourceId = activeReport.object?.id;

      await createSubTradeRequests({
        variables: {
          input: {
            source,
            sourceId,
            subTradeRequests,
          },
        },
      });

      if (bulkTradingProcess === 'BUCKET') {
        if (sourceId) {
          try {
            await bucketSubTradeRequests({
              variables: {
                input: {
                  source,
                  sourceId,
                  ...(organizationId && {
                    organizationId,
                  }),
                },
              },
            });
          } catch (bucketErr) {
            console.warn(
              'Bucketing failed (trades were still created):',
              bucketErr,
            );
          }
        }
      }

      enqueueSnackbar(
        t('portfoliosTable.tradeToasts.created', {
          count: subTradeRequests.length,
        }),
        { variant: 'success' },
      );
      setShowTradeBlotter(false);
      onTradeComplete();
    } catch {
      enqueueSnackbar(t('portfoliosTable.tradeToasts.createFailed'), {
        variant: 'error',
      });
    }
  };

  return {
    showTradeBlotter,
    setShowTradeBlotter,
    openBlotter,
    blotterLoading,
    strLoading,
    blotterReport,
    activeSubTradeRequests,
    onRefreshActiveSTRs,
    onGenerateTrades,
  };
}
